import { ethers } from "ethers";

import { ABI_REGISTRY } from "src/contracts/abiRegistry";

const ABI_LIST = [
  ABI_REGISTRY.BrazilCarbonCreditUnified,
  ABI_REGISTRY.AgroforestryProject,
  ABI_REGISTRY.CarbonCreditPrivateOffering,
  ABI_REGISTRY.CarbonCreditPublicSale,
  ABI_REGISTRY.CarbonCreditInvestmentToken,
  ABI_REGISTRY.AgroforestryProjectFactory,
  ABI_REGISTRY.BrazilCarbonKYCWhitelist,
  ABI_REGISTRY.BrazilCarbonUniversalCredit1155,
  ABI_REGISTRY.USDC,
  ABI_REGISTRY.BCTToken,
].map((item) => item?.abi || item);

// Custom error selectors → user-friendly messages
const KNOWN_ERROR_MESSAGES = {
  "0xc0c5d8a1": "KYC verification is required. Please complete identity verification before proceeding.",
};

const ERROR_SELECTOR = "0x08c379a0";
const PANIC_SELECTOR = "0x4e487b71";
const REVERT_REASON_REGEX = /reverted with reason string ['"]([^'"]+)['"]/i;
let cachedInterfaces = null;

const getInterfaces = () => {
  if (cachedInterfaces) {
    return cachedInterfaces;
  }
  const InterfaceCtor = ethers?.utils?.Interface;
  if (typeof InterfaceCtor !== "function") {
    cachedInterfaces = [];
    return cachedInterfaces;
  }
  cachedInterfaces = ABI_LIST.map((abi) => {
    try {
      return new InterfaceCtor(abi);
    } catch {
      return null;
    }
  }).filter(Boolean);
  return cachedInterfaces;
};

const PANIC_CODES = {
  0x01: "Assertion violated",
  0x11: "Arithmetic overflow/underflow",
  0x12: "Division by zero",
  0x21: "Invalid enum value",
  0x22: "Invalid storage byte array encoding",
  0x31: "Pop on empty array",
  0x32: "Array index out of bounds",
  0x41: "Memory allocation overflow",
  0x51: "Zero-initialized function pointer",
};

const ERROR_LABELS = {
  "ACTIVE": "Active",
  "ALLOCATION_MISMATCH": "Allocation Mismatch",
  "ALLOWANCE": "Allowance",
  "ALREADY_APPROVED": "Already Approved",
  "ALREADY_AUDITED": "Already Audited",
  "ALREADY_CONFIGURED": "Already Configured",
  "ALREADY_FAILED": "Already Failed",
  "ALREADY_FINALIZED": "Already Finalized",
  "ALREADY_INIT": "Already Init",
  "ALREADY_SIGNED": "Already Signed",
  "ALREADY_WITHDRAWN": "Already Withdrawn",
  "AMOUNT_TOO_LOW": "Amount Too Low",
  "APPROVE_FAILED": "Approve Failed",
  "AUDITOR_IS_DEVELOPER": "Auditor cannot be the developer",
  "AUDIT_MARK_FAILED": "Audit Mark Failed",
  "AUTH": "Unauthorized",
  "AUTH_ADMIN": "Admin only",
  "AUTH_AUDITOR": "Auditor only",
  "AUTH_CERTIFIER": "Certifier only",
  "AUTH_DEVELOPER": "Developer only",
  "AUTH_FACTORY": "Factory only",
  "AUTH_FACTORY_OWNER": "Factory owner only",
  "AUTH_FARMER": "Farmer only",
  "AUTH_MAIN_OWNER": "Main owner only",
  "AUTH_OWNER": "Owner only",
  "AUTH_PARTNER": "Developer only",
  "AUTH_TOKEN": "Token only",
  "AUTH_VALIDATION": "Validation auditor only",
  "AUTH_VERIFICATION": "Verification auditor only",
  "BAD_FUNDER": "Bad Funder",
  "BAD_INDEX": "Bad Index",
  "BAD_INPUT": "Bad Input",
  "BAD_PRICE_PRECISION": "Bad Price Precision",
  "BAD_TO": "Bad To",
  "BALANCE": "Balance",
  "BALANCE_MISMATCH": "Balance Mismatch",
  "CANNOT_REMOVE_MAIN": "Cannot Remove Main",
  "CLOSED": "Closed",
  "CLOSE_AFTER_CLAIM": "Close After Claim",
  "KYC_REQUIRED": "KYC verification is required. Please complete identity verification before proceeding.",
  "CONFIG_EXISTS": "Config Exists",
  "CONFIG_LOCKED": "Config Locked",
  "CONFIG_SPECIES": "Config Species",
  "CONFIG_VERSION": "Config Version",
  "CONFIG_YEAR": "Config Year",
  "CREDITS_NOT_RECEIVED": "Credits Not Received",
  "CREDIT_TOKEN_NOT_SET": "Credit Token Not Set",
  "DEPOSITS_CLOSED": "Deposits Closed",
  "DEPOSIT_FAILED": "Deposit Failed",
  "DEPOSIT_REGISTRATION_FAILED": "Deposit Registration Failed",
  "DEVELOPER_BLOCKED": "Developer Blocked",
  "E28": "E28",
  "E71": "E71",
  "E75": "E75",
  "E76": "E76",
  "EMPTY": "Empty",
  "EMPTY_QUEUE": "Empty Queue",
  "EXCEEDS_ALLOCATION": "Exceeds Allocation",
  "EXCEEDS_REMAINING": "Exceeds Remaining",
  "EXCEEDS_STAGE_CAP": "Exceeds Stage Cap",
  "E_AMOUNT": "E Amount",
  "E_EMPTY": "E Empty",
  "E_MAX_BATCH": "E Max Batch",
  "E_POINTER": "E Pointer",
  "E_PREDICTED": "E Predicted",
  "E_PROJECT": "E Project",
  "E_SPECIES": "E Species",
  "E_STABLE": "E Stable",
  "E_STAGE": "E Stage",
  "E_TO": "E To",
  "E_TOKEN": "E Token",
  "E_YEAR": "E Year",
  "FARMER_BLOCKED": "Farmer Blocked",
  "FINALIZED": "Finalized",
  "FINALIZE_BEFORE_CLAIM": "Finalize Before Claim",
  "FINALIZE_FAILED": "Finalize Failed",
  "GAS_LIMIT_EXCEEDED": "Gas Limit Exceeded",
  "HIGH_COMMISSION": "High Commission",
  "INDEX_OUT_OF_BOUNDS": "Index Out Of Bounds",
  "INIT_DONE": "Init Done",
  "INSUFFICIENT": "Insufficient",
  "INSUFFICIENT_AVAILABLE": "Insufficient Available",
  "INSUFFICIENT_BALANCE": "Insufficient balance",
  "INSUFFICIENT_CREDITS": "Insufficient Credits",
  "INSUFFICIENT_FUNDS": "Insufficient funds",
  "INSUFFICIENT_STAGE_RESERVE": "Insufficient Stage Reserve",
  "INSUFFICIENT_TOTAL_RESERVE": "Insufficient Total Reserve",
  "INVALID": "Invalid",
  "INVALID_SPECIES": "Invalid species",
  "INVALID_STAGE": "Invalid stage",
  "INVALID_STAGES": "Invalid Stages",
  "INVALID_YEAR": "Invalid year",
  "INVALID_YEARS": "Invalid Years",
  "Initialized": "Initialized",
  "LEN": "Len",
  "LEN_COSTS": "Len Costs",
  "LEN_PRICES": "Len Prices",
  "LEN_TYPES": "Len Types",
  "LOCKED": "Locked",
  "MARK_FAILED_FAILED": "Mark Failed Failed",
  "MAX_BATCH": "Max Batch",
  "MAX_COST": "Max Cost",
  "MAX_PRICE": "Max Price",
  "MAX_REVENUE": "Max Revenue",
  "MAX_SPECIES": "Max Species",
  "MAX_SUPPLY_ERROR": "Max Supply Error",
  "MINT_FAILED": "Mint Failed",
  "MINT_MISMATCH": "Mint Mismatch",
  "NOTHING_TO_CANCEL": "Nothing To Cancel",
  "NOT_ACTIVE": "Not Active",
  "NOT_APPROVED": "Project not approved",
  "NOT_AUDITED": "Stage not audited",
  "NOT_CLAIMABLE": "Not Claimable",
  "NOT_CONFIGURED": "Project not configured",
  "NOT_ENDED": "Not Ended",
  "NOT_ERC1155": "Not Erc1155",
  "NOT_ERC20": "Not Erc20",
  "NOT_FAILED": "Not Failed",
  "NOT_FINALIZED": "Not Finalized",
  "NOT_IMPLEMENTED": "Not Implemented",
  "NOT_INACTIVE": "Not Inactive",
  "NOT_LIVE": "Not Live",
  "NOT_MATURED": "Not Matured",
  "NOT_OWNER": "Not Owner",
  "NOT_PROJECT": "Not Project",
  "NOT_REDEEMABLE": "Not Redeemable",
  "NOT_SIGNED": "Stage not signed",
  "NO_ACTIVE_SPECIES": "No Active Species",
  "NO_CHANGE": "No Change",
  "NO_COMMISSION": "No Commission",
  "NO_FINALIZE_DATA": "No Finalize Data",
  "NO_FUNDS": "No Funds",
  "NO_LISTING": "No Listing",
  "NO_LOGIC": "No Logic",
  "NO_PROJECTS": "No Projects",
  "NO_PROJECT_LOGIC": "No Project Logic",
  "NO_SHARES": "No Shares",
  "NO_STABLECOIN": "No Stablecoin",
  "NO_STAKE": "No Stake",
  "NO_SUPPLY": "No Supply",
  "NO_TOKENS": "No Tokens",
  "NO_TOKEN_LOGIC": "No Token Logic",
  "NO_USDC": "No Usdc",
  "ONLY_OWNER": "Only Owner",
  "ONLY_TOKEN": "Only Token",
  "PAUSE_FAILED": "Pause Failed",
  "POOL_EMPTY": "Pool Empty",
  "POOL_EXISTS": "Pool Exists",
  "POOL_INIT_FAILED": "Pool Init Failed",
  "PRICE_TOO_LOW": "Price Too Low",
  "PRINCIPAL_TOO_LARGE": "Principal Too Large",
  "PRINCIPAL_ZERO": "Principal Zero",
  "PROJECT_ENDED": "Project Ended",
  "PROJECT_FAILED": "Project Failed",
  "PROJECT_INIT_FAILED": "Project Init Failed",
  "PROJECT_NOT_APPROVED": "Project Not Approved",
  "RESERVED_FUNDS": "Reserved Funds",
  "SLIPPAGE": "Slippage",
  "SPECIES_LEN": "Species Len",
  "SPECIES_NOT_CONFIGURED": "Species Not Configured",
  "STAGE_AUDITED": "Stage Audited",
  "STAGE_DONE": "Stage Done",
  "STAGE_LEN": "Stage Len",
  "STAGE_NOT_FUNDED": "Stage Not Funded",
  "STAGE_PARAMS_MISSING": "Stage Params Missing",
  "TOKENS_EXIST": "Tokens Exist",
  "TOKEN_ALREADY_SET": "Token Already Set",
  "TOKEN_CONFIG_FAILED": "Token Config Failed",
  "TOKEN_CREDIT_CONFIG_FAILED": "Token Credit Config Failed",
  "TOKEN_INIT_FAILED": "Token Init Failed",
  "TOKEN_NOT_SET": "Token Not Set",
  "TOO_EARLY": "Too Early",
  "UNCLAIMED": "Unclaimed",
  "UNPAUSE_FAILED": "Unpause Failed",
  "UNREDEEMED": "Unredeemed",
  "WRONG_CLAIM_TS": "Wrong Claim Ts",
  "WRONG_SPECIES": "Wrong Species",
  "WRONG_STABLE": "Wrong Stable",
  "WRONG_STAGE": "Wrong Stage",
  "WRONG_TIMESTAMP": "Wrong Timestamp",
  "WRONG_YEAR": "Wrong Year",
  "YEAR_FINALIZED": "Year Finalized",
  "YEAR_LEN": "Year Len",
  "YEAR_NOT_CONFIGURED": "Year Not Configured",
  "ZERO": "Zero",
  "ZERO_ADDR": "Invalid address",
  "ZERO_ADDRESS": "Invalid address",
  "ZERO_AMOUNT": "Amount must be greater than zero",
  "ZERO_CONFIG": "Missing configuration",
  "ZERO_DEVELOPER": "Zero Developer",
  "ZERO_FARMER": "Zero Farmer",
  "ZERO_FUNDING": "Zero Funding",
  "ZERO_HALFLIFE": "Zero Halflife",
  "ZERO_PREDICTED": "Zero Predicted",
  "ZERO_PROJECT": "Zero Project",
  "ZERO_REFUND": "Zero Refund",
  "ZERO_STABLE": "Stablecoin not set",
  "ZERO_TOKEN": "Token not set",
  "ZERO_AUDITOR": "Auditor address required",
};

const formatErrorName = (name) => {
  if (!name || typeof name !== "string") {
    return null;
  }
  if (ERROR_LABELS[name]) {
    return ERROR_LABELS[name];
  }
  return name
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const decodeStandardError = (data) => {
  if (!data || typeof data !== "string") {
    return null;
  }
  if (data.startsWith(ERROR_SELECTOR)) {
    try {
      const decoded = ethers.utils.defaultAbiCoder.decode(
        ["string"],
        `0x${data.slice(10)}`
      );
      return decoded?.[0] || "Execution reverted";
    } catch {
      return "Execution reverted";
    }
  }
  if (data.startsWith(PANIC_SELECTOR)) {
    try {
      const decoded = ethers.utils.defaultAbiCoder.decode(
        ["uint256"],
        `0x${data.slice(10)}`
      );
      const code = decoded?.[0]?.toNumber?.() ?? Number(decoded?.[0]);
      const msg = PANIC_CODES[code] || `Panic ${code}`;
      return `Panic: ${msg}`;
    } catch {
      return "Panic";
    }
  }
  return null;
};

const extractReasonFromMessage = (message) => {
  if (typeof message !== "string") {
    return null;
  }
  const match = message.match(REVERT_REASON_REGEX);
  if (match && match[1]) {
    return match[1];
  }
  return null;
};

const tryParseErrorData = (data) => {
  if (!data || typeof data !== "string" || !data.startsWith("0x")) {
    return null;
  }
  const standard = decodeStandardError(data);
  if (standard) {
    return standard;
  }
  // Check known error selectors (4 bytes)
  const selector = data.slice(0, 10).toLowerCase();
  if (KNOWN_ERROR_MESSAGES[selector]) {
    return KNOWN_ERROR_MESSAGES[selector];
  }
  for (const iface of getInterfaces()) {
    try {
      const parsed = iface.parseError(data);
      if (parsed?.name) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.debug("[contract-error]", parsed.name, parsed.args || []);
        }
        const label = formatErrorName(parsed.name) || parsed.name;
        if (parsed.args && parsed.args.length) {
          return `${label} (${parsed.args.join(", ")})`;
        }
        return label;
      }
    } catch {
      // ignore
    }
  }
  return null;
};

const extractHexData = (error) => {
  if (!error || typeof error !== "object") {
    return null;
  }
  const candidates = [
    error.data,
    error.error?.data,
    error.error?.error?.data,
    error.data?.data,
    error.data?.originalError?.data,
    error.error?.data?.originalError?.data,
    error.error?.error?.data?.originalError?.data,
    error.receipt?.revertReason,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.startsWith("0x")) {
      return candidate;
    }
    if (candidate && typeof candidate === "object") {
      const inner = candidate.data || candidate.originalError?.data;
      if (typeof inner === "string" && inner.startsWith("0x")) {
        return inner;
      }
    }
  }
  return null;
};

export const decodeContractError = (error) => {
  const data = extractHexData(error);
  const parsed = tryParseErrorData(data);
  if (parsed) {
    return parsed;
  }
  if (typeof error?.reason === "string") {
    return error.reason;
  }
  if (typeof error?.message === "string" && error.message.includes("execution reverted")) {
    return extractReasonFromMessage(error.message) || error.message;
  }
  return null;
};
